import React from 'react';
import {
    Grid,
    Typography,
    Box,
    Switch,
    TextField,
    CircularProgress,
    FormHelperText,
    Autocomplete
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { FeeScheduleSelection } from "../../../feeSchedulePage/feeScheduleSelectionComponent";
import TruncatedText from './TruncatedText';
import StateDropdown from '../../RulesEditPage/components/StateDropdown';
import LocalityDropdown from '../../RulesEditPage/components/LocalityDropdown';
import GPCIDropdown from '../../RulesEditPage/components/GPCIDropdown';
import { GPCILocalityData } from '../../../../store/api/globalFeeSchedule';

interface LesserOfMappingStepProps {
    values: any;
    touched: any;
    errors: any;
    classes: any;
    isCopyNewName: (key: string) => boolean;
    handleCopyModeToggle: (stepKey: string, index: number, checked: boolean, groupPath: string, group: any, setFieldValue: any) => void;
    setFieldValue: (field: string, value: any) => void;
    targetClient: any;
    handleTargetLesserOfFeeScheduleChange: (setFieldValue: any, groupIndex: number, ruleIndex: number, obj: any, groupType: string) => void;
    handleTargetLesserOfLabelChange: (setFieldValue: any, groupIndex: number, ruleIndex: number, obj: any, rule: any, groupType: string) => void;
    handleNewLesserOfFeeScheduleChange: (setFieldValue: any, index: number, val: any, groupType: string) => void;
    handleBlur: (e: any) => void;
    getLesserOfHelperText: (touched: any, key: string, index: number, ruleIndex?: number, type?: string) => any;
    getLesserOfError: (errors: any, key: string, index: number, ruleIndex?: number, type?: string) => any;
    nameExistErrors: any;
    nameValidatePending: any;
    gpciLocalityData: GPCILocalityData[];
    feeScheduleLabels: any;
    fetchItemsBasedOnTargetClient: (rule: any) => void;
    feeScheduleLabelsValidate: any;
    getFeeScheduleLabelsFromTargetClientsPending: boolean;
}

const LesserOfMappingStep: React.FC<LesserOfMappingStepProps> = ({
    values,
    touched,
    errors,
    classes,
    isCopyNewName,
    handleCopyModeToggle,
    setFieldValue,
    targetClient,
    handleTargetLesserOfFeeScheduleChange,
    handleTargetLesserOfLabelChange,
    handleNewLesserOfFeeScheduleChange,
    handleBlur,
    getLesserOfHelperText,
    getLesserOfError,
    nameExistErrors,
    nameValidatePending,
    gpciLocalityData,
    feeScheduleLabels,
    fetchItemsBasedOnTargetClient,
    feeScheduleLabelsValidate,
    getFeeScheduleLabelsFromTargetClientsPending
}) => {
    return (
        <>
            {values?.lesserOfMapping?.localFeeSchedules?.length > 0 && <Typography sx={{ fontSize: "20px", fontWeight: "bold", ml: "10px", mb: "20px" }}>Local Fee Schedules</Typography>}

            {values?.lesserOfMapping?.localFeeSchedules?.map((group, index) => (
                <Grid container className='pb-4 align-items-start' key={index} sx={{ borderBottom: '1px solid #f0f0f0', pt: 2, px: 1 }}>
                    <Grid item xs={12} sx={{ mb: 1.5 }}>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <span className='font-12'><b>Fee Schedule :</b> <TruncatedText text={group.feeScheduleName} fixedWidth={250} /></span>
                            <Box display="flex" alignItems="center" sx={{ ml: 4 }}>
                                <Typography variant="caption" sx={{ color: isCopyNewName(`step-lesserof-local-${index}`) ? 'text.primary' : 'text.secondary', fontWeight: isCopyNewName(`step-lesserof-local-${index}`) ? 'normal' : 'bold', mr: 1, fontSize: "13px" }}>Select Fee Schedule At Target</Typography>
                                <Switch
                                    size="small"
                                    checked={isCopyNewName(`step-lesserof-local-${index}`)}
                                    onChange={(e) => handleCopyModeToggle('step-lesserof-local', index, e.target.checked, 'lesserOfMapping.localFeeSchedules', group, setFieldValue)}
                                    color="primary"
                                />
                                <Typography variant="caption" sx={{ color: !isCopyNewName(`step-lesserof-local-${index}`) ? 'text.primary' : 'text.secondary', fontWeight: !isCopyNewName(`step-lesserof-local-${index}`) ? 'normal' : 'bold', ml: 1, fontSize: "13px" }}>Copy Fee Schedule From Source</Typography>
                            </Box>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {group.rules?.map((rule, ruleIndex) => (
                            <Grid container item xs={12} key={ruleIndex} spacing={2} sx={{ mb: 4, alignItems: 'start' }}>
                                {/* Left Column: Rule Info and Fee Schedule Selection */}
                                <Grid item xs={8}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingLeft: '20px' }}>
                                        <div style={{ minWidth: '180px' }}>
                                            <div className='font-12'><b>Rule :</b> <TruncatedText text={rule.ruleName} fixedWidth={180} /></div>
                                            {rule.percentage && <div className='font-12'><b>Percentage : </b> {rule.percentage}%</div>}
                                            {rule.sourceLabel && <div className='font-12'><b>Rate Label : </b> <TruncatedText text={rule.sourceLabel} fixedWidth={180} /></div>}
                                        </div>
                                        {!isCopyNewName(`step-lesserof-local-${index}`) && (
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <FeeScheduleSelection
                                                    deletable={!!rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId?.id !== 0}
                                                    feeScheduleType='Local'
                                                    hideTypeToggle={true}
                                                    targetClient={targetClient?.schema}
                                                    defaultValue='Select Fee Schedule At Target'
                                                    handleSetFeeSchedule={(rowData) => handleTargetLesserOfFeeScheduleChange(setFieldValue, index, ruleIndex, rowData, 'localFeeSchedules')}
                                                    value={rule.targetFeeScheduleId?.name}
                                                />
                                                {(getLesserOfHelperText(touched, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules') && getLesserOfError(errors, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules')) && (
                                                    <FormHelperText error style={{ marginLeft: '14px' }}>
                                                        {getLesserOfError(errors, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules')}
                                                    </FormHelperText>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Grid>

                                {/* Right Column: New FS Name / Target Label / RBRVS */}
                                <Grid item xs={4}>
                                    {ruleIndex === 0 && isCopyNewName(`step-lesserof-local-${index}`) && (
                                        <Box sx={{ position: 'relative', mb: 2 }}>
                                            <TextField
                                                required
                                                label="New Fee Schedule Name"
                                                name={`lesserOfMapping.localFeeSchedules.${index}.newFeeScheduleName`}
                                                className={classes.w90}
                                                value={group.newFeeScheduleName}
                                                onBlur={handleBlur}
                                                onChange={e => handleNewLesserOfFeeScheduleChange(setFieldValue, index, e.target.value, 'localFeeSchedules')}
                                                helperText={getLesserOfError(errors, 'newFeeScheduleName', index, undefined, 'localFeeSchedules') || nameExistErrors[`step-lesserof-localFeeSchedules-${index}`]}
                                                error={Boolean(getLesserOfError(errors, 'newFeeScheduleName', index, undefined, 'localFeeSchedules') || nameExistErrors[`step-lesserof-localFeeSchedules-${index}`])}
                                            />
                                            {nameValidatePending[`step-lesserof-localFeeSchedules-${index}`] === true ? (
                                                <Box className={classes.iconsContainer} sx={{ right: '12%' }}><CircularProgress size={10} /></Box>
                                            ) : nameValidatePending[`step-lesserof-localFeeSchedules-${index}`] === false && (
                                                <Box className={classes.iconsContainer} sx={{ right: '12%' }}>
                                                    {nameExistErrors[`step-lesserof-localFeeSchedules-${index}`] ? <CloseIcon className={classes.errorIcon} /> : <CheckIcon className={classes.successIcon} />}
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {(!isCopyNewName(`step-lesserof-local-${index}`) && !!rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId.id !== 0) && (
                                        <Box sx={{ mb: 2, width: '90%' }}>
                                            <Autocomplete
                                                onChange={(e, value) => handleTargetLesserOfLabelChange(setFieldValue, index, ruleIndex, value, rule, 'localFeeSchedules')}
                                                className={classes.w90}
                                                value={rule.targetLabelId}
                                                options={feeScheduleLabels[rule.targetFeeScheduleId.id] || []}
                                                onOpen={() => fetchItemsBasedOnTargetClient(rule)}
                                                noOptionsText={getFeeScheduleLabelsFromTargetClientsPending ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={10} /></Box> : 'No options'}
                                                getOptionLabel={(option) => option.label || option.name || ''}
                                                isOptionEqualToValue={(option, value) => (value?.label || value?.name) === (option.label || option.name)}
                                                renderInput={params => (
                                                    <Box style={{ position: 'relative' }}>
                                                        <TextField
                                                            {...params}
                                                            label="Target Rate Label *"
                                                            fullWidth
                                                            helperText={getLesserOfError(errors, 'targetLabelId', index, ruleIndex, 'localFeeSchedules')}
                                                            error={Boolean(getLesserOfError(errors, 'targetLabelId', index, ruleIndex, 'localFeeSchedules'))}
                                                        />
                                                        {feeScheduleLabelsValidate[`pending-lesserof-localFeeSchedules-${index}-${ruleIndex}`] === true ? (
                                                            <Box className={classes.iconsContainer}><CircularProgress size={10} /></Box>
                                                        ) : feeScheduleLabelsValidate[`pending-lesserof-localFeeSchedules-${index}-${ruleIndex}`] === false && (
                                                            <Box className={classes.iconsContainer}>
                                                                {feeScheduleLabelsValidate[`done-lesserof-localFeeSchedules-${index}-${ruleIndex}`] ? <CloseIcon className={classes.errorIcon} /> : <CheckIcon className={classes.successIcon} />}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                                disableClearable
                                            />
                                        </Box>
                                    )}

                                    {(rule.isRBRVS || rule?.isDME) && (
                                        <Box sx={{ mt: 1, mb: 4, display: 'flex', flexDirection: 'column', gap: '16px', width: '90%' }}>
                                            <div className='font-12'><b>Rule :</b> {rule.ruleName}</div>
                                            <StateDropdown
                                                scheduleId={group.sourceFeeScheduleId}
                                                value={rule.selectedState}
                                                onChange={(value) => {
                                                    setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, value);
                                                    if (value !== rule.selectedState) {
                                                        setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, null);
                                                        setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, rule.selectedState || '')}
                                                error={getLesserOfError(errors, 'selectedState', index, ruleIndex, 'localFeeSchedules')}
                                                required={true}
                                            />
                                            <LocalityDropdown
                                                selectedState={rule.selectedState}
                                                selectedGpci={rule.selectedGpci}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedLocality}
                                                onChange={(value) => setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, value)}
                                                onBlur={() => setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, rule.selectedLocality || '')}
                                                error={getLesserOfError(errors, 'selectedLocality', index, ruleIndex, 'localFeeSchedules')}
                                                required={true}
                                            />
                                            <GPCIDropdown
                                                selectedState={rule.selectedState}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedGpci}
                                                onChange={(value) => {
                                                    setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, value);
                                                    if (value !== rule.selectedGpci) {
                                                        setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`lesserOfMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, rule.selectedGpci || '')}
                                                error={getLesserOfError(errors, 'selectedGpci', index, ruleIndex, 'localFeeSchedules')}
                                                required={true}
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}

            {values?.lesserOfMapping?.globalFeeSchedules?.length > 0 && <Typography sx={{ fontSize: "20px", fontWeight: "bold", ml: "10px", mb: "20px",mt:"20px" }}>Global Fee Schedules</Typography>}
            {values?.lesserOfMapping?.globalFeeSchedules?.map((group, index) => (
                <Grid container className='pb-4 align-items-start' key={index} sx={{ borderBottom: '1px solid #f0f0f0', pt: 2, px: 1 }}>
                    <Grid item xs={12} sx={{ mb: 1.5 }}>
                        <div style={{ marginTop: '8px' }}>
                            <span className='font-12'><b>Fee Schedule :</b> <TruncatedText text={group.feeScheduleName} fixedWidth={250} /></span>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {group.rules?.map((rule, ruleIndex) => (
                            <Grid container item xs={12} key={ruleIndex} spacing={2} sx={{ mb: 4, alignItems: 'start' }}>
                                {/* Left Column: Rule Info */}
                                <Grid item xs={8}>
                                    <div style={{ paddingLeft: '20px' }}>
                                        <div className='font-12'><b>Rule :</b> <TruncatedText text={rule.ruleName} fixedWidth={180} /></div>
                                        {rule.percentage && <div className='font-12'><b>Percentage :</b> {rule.percentage}%</div>}
                                        {rule.sourceLabel && <div className='font-12'><b>Rate Label :</b> <TruncatedText text={rule.sourceLabel} fixedWidth={180} /></div>}
                                    </div>
                                </Grid>

                                {/* Right Column: Target Rate Label / RBRVS */}
                                <Grid item xs={4}>
                                    <Box sx={{ mb: 2, width: '90%' }}>
                                        {/* <Autocomplete
                                            onChange={(e, value) => handleTargetLesserOfLabelChange(setFieldValue, index, ruleIndex, value, rule, 'globalFeeSchedules')}
                                            className={classes.w90}
                                            value={rule.targetLabelId}
                                            options={feeScheduleLabels[rule.targetFeeScheduleId.id] || []}
                                            onOpen={() => fetchItemsBasedOnTargetClient(rule)}
                                            noOptionsText={getFeeScheduleLabelsFromTargetClientsPending ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={10} /></Box> : 'No options'}
                                            getOptionLabel={(option) => option.label || option.name || ''}
                                            isOptionEqualToValue={(option, value) => (value?.label || value?.name) === (option.label || option.name)}
                                            renderInput={params => (
                                                <Box style={{ position: 'relative' }}>
                                                    <TextField
                                                        {...params}
                                                        label="Target Rate Label *"
                                                        fullWidth
                                                        helperText={getLesserOfError(errors, 'targetLabelId', index, ruleIndex, 'globalFeeSchedules')}
                                                        error={Boolean(getLesserOfError(errors, 'targetLabelId', index, ruleIndex, 'globalFeeSchedules'))}
                                                    />
                                                    {feeScheduleLabelsValidate[`pending-lesserof-globalFeeSchedules-${index}-${ruleIndex}`] === true ? (
                                                        <Box className={classes.iconsContainer}><CircularProgress size={10} /></Box>
                                                    ) : feeScheduleLabelsValidate[`pending-lesserof-globalFeeSchedules-${index}-${ruleIndex}`] === false && (
                                                        <Box className={classes.iconsContainer}>
                                                            {feeScheduleLabelsValidate[`done-lesserof-globalFeeSchedules-${index}-${ruleIndex}`] ? <CloseIcon className={classes.errorIcon} /> : <CheckIcon className={classes.successIcon} />}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                            disableClearable
                                        /> */}
                                    </Box>

                                    {(rule.isRBRVS || rule?.isDME) && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '90%' }}>
                                            <StateDropdown
                                                scheduleId={group.sourceFeeScheduleId}
                                                value={rule.selectedState}
                                                onChange={(value) => {
                                                    setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, value);
                                                    if (value !== rule.selectedState) {
                                                        setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, null);
                                                        setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, rule.selectedState || '')}
                                                error={getLesserOfError(errors, 'selectedState', index, ruleIndex, 'globalFeeSchedules')}
                                                required={true}
                                            />
                                           {rule.isRBRVS && <GPCIDropdown
                                                selectedState={rule.selectedState}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedGpci}
                                                onChange={(value) => {
                                                    setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, value);
                                                    if (value !== rule.selectedGpci) {
                                                        setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                    }
                                                }}
                                                onBlur={() => setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, rule.selectedGpci || '')}
                                                error={getLesserOfError(errors, 'selectedGpci', index, ruleIndex, 'globalFeeSchedules')}
                                                required={true}
                                            />}
                                           {rule.isRBRVS && <LocalityDropdown
                                                selectedState={rule.selectedState}
                                                selectedGpci={rule.selectedGpci}
                                                gpciLocalityData={gpciLocalityData}
                                                value={rule.selectedLocality}
                                                onChange={(value) => setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, value)}
                                                onBlur={() => setFieldValue(`lesserOfMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, rule.selectedLocality || '')}
                                                error={getLesserOfError(errors, 'selectedLocality', index, ruleIndex, 'globalFeeSchedules')}
                                                required={true}
                                            />}
                                            
                                        </div>
                                    )}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}

            {(values.lesserOfMapping?.localFeeSchedules?.length === 0 && values.lesserOfMapping?.globalFeeSchedules?.length === 0) && (
                <Grid className='text-center'> <span>No Lesser of Configuration available</span> </Grid>
            )}
        </>
    );
};

export default LesserOfMappingStep;
